import React from "react";
import { createPortal } from "react-dom";
import { Loader2, Clock, AlertTriangle, CircleSlash } from "lucide-react";

// Central viewport-fixed modal. Renders through a PORTAL to document.body so it
// escapes any transformed/overflow ancestor (e.g. the page-entrance `.vda-rise`
// transform on <main>, which otherwise turns position:fixed into position:absolute).
// It therefore always appears centred in the CURRENT viewport regardless of page
// scroll. Locks background scroll, closes on Escape / backdrop, moves focus in and
// restores it on close, and scrolls internally when the content is tall.
export function ModalShell({ onClose, children, className = "", testId }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const opener = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose?.(); } };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => {
      const el = ref.current?.querySelector("[data-autofocus],input,select,textarea,button,a[href]");
      try { el?.focus(); } catch (_) { /* ignore */ }
    }, 30);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      try { opener?.focus?.(); } catch (_) { /* ignore */ }
    };
  }, [onClose]);
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center overflow-y-auto p-4 bg-black/40 backdrop-blur-sm"
      onMouseDown={onClose} role="dialog" aria-modal="true" data-testid={testId}
    >
      <div
        ref={ref} onMouseDown={(e) => e.stopPropagation()}
        className={`relative my-auto w-full max-w-lg max-h-[90vh] overflow-y-auto bg-card rounded-xl border border-border shadow-xl ${className}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function Panel({ className = "", children, ...rest }) {
  return <div className={`vda-panel ${className}`} {...rest}>{children}</div>;
}

export function Btn({ variant = "primary", className = "", loading, children, ...rest }) {
  const base =
    "inline-flex items-center justify-center gap-2 px-3.5 h-10 text-sm font-medium rounded-[6px] transition-colors disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-2";
  const variants = {
    primary: "bg-primary text-primary-foreground hover:bg-[hsl(var(--vda-green-hover))]",
    // emphasised secondary — quiet green surface, green ink (distinct from solid primary)
    clay: "bg-[hsl(var(--vda-green-bg))] text-primary border border-[hsl(var(--primary))]/25 hover:bg-[hsl(var(--vda-green-bg))]/70",
    outline: "border border-[hsl(var(--input))] bg-card hover:bg-secondary text-foreground",
    ghost: "hover:bg-secondary text-foreground",
    danger: "bg-[hsl(var(--vda-red-bg))] text-[hsl(var(--vda-red))] border border-[hsl(var(--vda-red))]/25 hover:bg-[hsl(var(--vda-red))]/10",
  };
  return (
    <button className={`${base} ${variants[variant]} ${className}`} disabled={loading || rest.disabled} {...rest}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block vda-tick text-muted-foreground">{label}</span>}
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-[hsl(var(--vda-red))]">{error}</span>}
    </label>
  );
}

export function Input({ className = "", ...rest }) {
  return (
    <input
      className={`w-full h-10 px-3 bg-card border border-[hsl(var(--input))] rounded-[6px] text-sm placeholder:text-muted-foreground/60 focus:border-primary ${className}`}
      {...rest}
    />
  );
}

export function Textarea({ className = "", ...rest }) {
  return (
    <textarea
      className={`w-full px-3 py-2 bg-card border border-[hsl(var(--input))] rounded-[6px] text-sm placeholder:text-muted-foreground/60 focus:border-primary ${className}`}
      {...rest}
    />
  );
}

export function Select({ className = "", children, ...rest }) {
  return (
    <select
      className={`w-full h-10 px-3 bg-card border border-[hsl(var(--input))] rounded-[6px] text-sm focus:border-primary ${className}`}
      {...rest}
    >
      {children}
    </select>
  );
}

// Compact status treatment: small square marker + text. A quiet background is
// applied only to genuinely urgent states (amber/red). Never colour-only.
const TONES = {
  green: { text: "text-primary", marker: "hsl(var(--vda-green))", box: "" },
  amber: { text: "text-[hsl(var(--vda-amber))]", marker: "hsl(var(--vda-amber))", box: "bg-[hsl(var(--vda-amber-bg))] border border-[hsl(var(--vda-amber))]/25" },
  red: { text: "text-[hsl(var(--vda-red))]", marker: "hsl(var(--vda-red))", box: "bg-[hsl(var(--vda-red-bg))] border border-[hsl(var(--vda-red))]/25" },
  clay: { text: "text-muted-foreground", marker: "hsl(var(--vda-relay))", box: "" },
  muted: { text: "text-muted-foreground", marker: "hsl(var(--vda-strong-border))", box: "" },
};

export function Tag({ tone = "muted", className = "", children }) {
  const t = TONES[tone] || TONES.muted;
  return (
    <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-[4px] text-xs font-medium ${t.text} ${t.box} ${className}`}>
      <span className="vda-marker" style={{ backgroundColor: t.marker }} aria-hidden="true" />
      {children}
    </span>
  );
}

export function Stat({ label, value, tone = "muted", testId }) {
  return (
    <Panel className="p-4" data-testid={testId}>
      <div className="vda-tick text-muted-foreground">{label}</div>
      <div className={`mt-1.5 text-2xl font-semibold tabular-nums ${tone === "red" ? "text-[hsl(var(--vda-red))]" : tone === "amber" ? "text-[hsl(var(--vda-amber))]" : "text-primary"}`}>
        {value}
      </div>
    </Panel>
  );
}

export function Spinner({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}…
    </div>
  );
}

export function EmptyState({ title, hint, action, reference }) {
  return (
    <div className="text-center py-12 px-4 border border-dashed border-[hsl(var(--input))] rounded-[8px]">
      <p className="font-medium text-foreground">{title}</p>
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
      {reference && <p className="mt-3 font-mono text-[11px] text-[hsl(var(--vda-quiet))]">{reference}</p>}
    </div>
  );
}

// Compact, accessible working-day deadline marker. State is never conveyed by
// colour alone: an icon + readable text + aria-label accompany every state.
const DL_STYLES = {
  not_due: "text-muted-foreground",
  due_soon: "text-[hsl(var(--vda-amber))] bg-[hsl(var(--vda-amber-bg))] border border-[hsl(var(--vda-amber))]/25",
  due_now: "text-[hsl(var(--vda-amber))] bg-[hsl(var(--vda-amber-bg))] border border-[hsl(var(--vda-amber))]/40 font-semibold",
  overdue: "text-[hsl(var(--vda-red))] bg-[hsl(var(--vda-red-bg))] border border-[hsl(var(--vda-red))]/30 font-semibold",
  none: "text-muted-foreground",
};

export function DeadlineBadge({ info, testId }) {
  if (!info) return null;
  const Icon = info.state === "overdue" ? AlertTriangle : info.state === "none" ? CircleSlash : Clock;
  return (
    <span
      role="status"
      aria-label={`Update deadline: ${info.srLabel}`}
      data-testid={testId}
      data-deadline-state={info.state}
      className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 text-xs rounded-[4px] whitespace-nowrap font-mono ${DL_STYLES[info.state] || DL_STYLES.not_due}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {info.label}
    </span>
  );
}
